import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BackgroundRunsProvider } from './context/BackgroundRunsContext';
import AuthPage from './pages/AuthPage';
import StrategyBuilder from './pages/StrategyBuilder';
import StrategyList from './pages/StrategyList';
import BacktestRunner from './pages/BacktestRunner';
import BacktestResults from './pages/BacktestResults';
import ProfilePage from './pages/ProfilePage';
import BillingPage from './pages/BillingPage';
import LandingPage from './pages/LandingPage';
import AnalystPage from './pages/AnalystPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PricingPage from './pages/PricingPage';
import HomePage from './pages/HomePage';
import RegimeTrackerPage from './pages/RegimeTrackerPage';
import StockPage from './pages/StockPage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
import SignupPrompt from './components/SignupPrompt';
import FeedbackModal from './components/FeedbackModal';
import NotificationCenter from './components/NotificationCenter';
import NavTickerSearch from './components/NavTickerSearch';
import NotificationsPage from './pages/NotificationsPage';
import MessagesPage from './pages/MessagesPage';
import MessageThreadPage from './pages/MessageThreadPage';
import PaperTradingPage from './pages/PaperTradingPage';
import PaperTradingDashboard from './pages/PaperTradingDashboard';
import WatchlistsPage from './pages/WatchlistsPage';
import MarketsPage from './pages/MarketsPage';
import AllBacktestsPage from './pages/AllBacktestsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ApiPage from './pages/ApiPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import Footer from './components/Footer';
import { ReactComponent as AlgovexIcon } from './assets/algovex-wordmark-dark.svg';
import { getBacktest } from './api';
import { track, Events } from './analytics';

function AppInner() {
  const { isAuthenticated, user, tier, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [editingStrategy, _setEditingStrategy] = useState(() => {
    try { const s = sessionStorage.getItem('editingStrategy'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const setEditingStrategy = (strategy) => {
    _setEditingStrategy(strategy);
    try {
      if (strategy) sessionStorage.setItem('editingStrategy', JSON.stringify(strategy));
      else sessionStorage.removeItem('editingStrategy');
    } catch {}
  };

  const [backtestResult, _setBacktestResult] = useState(() => {
    try { const s = sessionStorage.getItem('backtestResult'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const setBacktestResult = (result) => {
    _setBacktestResult(result);
    try {
      if (result) sessionStorage.setItem('backtestResult', JSON.stringify(result));
      else sessionStorage.removeItem('backtestResult');
    } catch {}
  };

  const [backtestStrategyId, setBacktestStrategyId] = useState(null);
  const [signupPrompt, setSignupPrompt] = useState(null);
  const [feedbackModal, setFeedbackModal] = useState(null); // null or {subject, message}
  const [showMobileNotice, setShowMobileNotice] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    const key = 'mobileDesktopNoticeDismissed';
    const evaluate = () => {
      const dismissed = sessionStorage.getItem(key) === '1';
      const isMobile = window.matchMedia('(max-width: 900px)').matches;
      setShowMobileNotice(isMobile && !dismissed);
    };

    evaluate();
    window.addEventListener('resize', evaluate);
    return () => window.removeEventListener('resize', evaluate);
  }, []);

  // Reset scroll to the top on every route change. Navigation mounts a fresh page,
  // but the main scroll container keeps its previous scrollTop — so e.g. running a
  // backtest (the Run button sits far down the long form) would drop you mid-page
  // on the results view. Reset the container, and window as a fallback.
  const mainScrollRef = React.useRef(null);
  React.useEffect(() => {
    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const dismissMobileNotice = () => {
    try { sessionStorage.setItem('mobileDesktopNoticeDismissed', '1'); } catch {}
    setShowMobileNotice(false);
  };

  // LandingPage is a self-contained public page with its own nav/hero;
  // the app chrome (nav, guest banner, mobile notice) would double up on top of it.
  const isGuestLanding = !isAuthenticated && location.pathname === '/';
  // Full-height "app shell" routes (chat / canvas) fill the viewport with their own
  // 100%-height layouts, so they must NOT be wrapped in the footer's grow-to-content
  // spacer (that spacer is height-indefinite and breaks their height:100% chain) and
  // must not render a footer.
  const isAppShellRoute = ['/analyst', '/builder'].includes(location.pathname);

  const handleFeedback = (subject = '', message = '') => setFeedbackModal({ subject, message });

  const handleEdit = (strategy) => {
    setEditingStrategy(strategy);
    navigate('/builder');
  };

  const handleNew = () => {
    if (!isAuthenticated) {
      setSignupPrompt('Sign up to create custom strategies.');
      return;
    }
    setEditingStrategy(null);
    navigate('/builder');
  };

  const handleBacktest = (strategy) => {
    setBacktestStrategyId(strategy?.id || null);
    navigate('/backtest');
  };

  const handleSaved = (strategy) => {
    // Single choke point for every save path in StrategyBuilder (create, update, force-update).
    track(Events.STRATEGY_SAVED, { strategy_id: strategy?.id ?? null });
    navigate('/strategies');
  };

  const handleResult = (result) => {
    setBacktestResult(result);
    navigate('/results');
  };

  const handleRequiresSignup = (message) => {
    setSignupPrompt(message || 'Sign up for a free account to continue.');
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Navigation */}
      {!isGuestLanding && (
      <nav className="app-nav" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '0 20px',
        height: 52,
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        color: 'var(--text)',
        flexShrink: 0,
      }}>
        <div
          className="nav-brand"
          style={{ marginRight: 24, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <AlgovexIcon style={{ width: 28, height: 28 }} />
          <span style={{ fontFamily: "'Oxanium', sans-serif", fontWeight: 800, fontSize: 18, letterSpacing: -0.5, color: 'var(--text)' }}>algo<span style={{ color: 'var(--teal)' }}>vex</span></span>
        </div>

        <button
          className="nav-hamburger"
          onClick={() => setMobileMenuOpen((o) => !o)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          style={{
            display: 'none',
            background: 'transparent',
            border: '1px solid var(--border)',
            color: 'var(--text)',
            borderRadius: 6,
            width: 34,
            height: 34,
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            cursor: 'pointer',
            marginLeft: 'auto',
          }}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>

        <div className={`nav-links${mobileMenuOpen ? ' nav-links-open' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
          {isAuthenticated && (<>
            <NavBtn active={location.pathname === '/'} onClick={() => navigate('/')}>Home</NavBtn>
            <NavBtn active={location.pathname === '/strategies'} onClick={() => navigate('/strategies')}>Strategies</NavBtn>
            <NavBtn active={location.pathname === '/builder'} onClick={handleNew}>+ New</NavBtn>
            <NavBtn active={location.pathname === '/backtest'} onClick={() => handleBacktest(null)}>Backtest</NavBtn>
            <NavBtn active={location.pathname === '/markets' || location.pathname.startsWith('/stock')} onClick={() => navigate('/markets')}>Markets</NavBtn>
            <NavBtn active={location.pathname === '/watchlists'} onClick={() => navigate('/watchlists')}>Watchlists</NavBtn>
            <NavBtn active={location.pathname === '/analyst'} onClick={() => navigate('/analyst')}>AI Analyst</NavBtn>
            <NavBtn active={location.pathname.startsWith('/paper-trading')} onClick={() => navigate('/paper-trading')}>Paper Trading</NavBtn>
            {backtestResult && (
              <NavBtn active={location.pathname === '/results'} onClick={() => navigate('/results')}>Results</NavBtn>
            )}
          </>)}
          {!isAuthenticated && location.pathname !== '/' && (
            <NavBtn active={location.pathname === '/strategies'} onClick={() => navigate('/strategies')}>Strategies</NavBtn>
          )}
          <div className="nav-spacer" style={{ flex: 1 }} />
          {isAuthenticated && (
            <NavBtn active={false} onClick={() => handleFeedback()}>Feedback</NavBtn>
          )}
          <NavBtn active={location.pathname.startsWith('/blog')} onClick={() => navigate('/blog')}>Blog</NavBtn>
          <NavBtn active={location.pathname === '/pricing'} onClick={() => navigate('/pricing')}>Pricing</NavBtn>
          <NavBtn active={location.pathname === '/api-access'} onClick={() => navigate('/api-access')}>API</NavBtn>
          {isAuthenticated && (
            <NavTickerSearch onNavigate={(path) => { navigate(path); setMobileMenuOpen(false); }} />
          )}
          {isAuthenticated ? (
            <div className="nav-session" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="nav-session-top" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <NotificationCenter onBacktestLoaded={handleResult} />
                <span
                  onClick={() => navigate('/profile')}
                  style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}
                >
                  {user.username}
                  {tier && tier !== 'free' && (
                    <span style={{
                      marginLeft: 6, padding: '2px 6px', fontSize: 10, fontWeight: 700,
                      background: tier === 'pro' ? 'var(--teal)' : 'var(--blue)',
                      borderRadius: 4, color: 'var(--bg)', textTransform: 'uppercase',
                    }}>{tier}</span>
                  )}
                </span>
              </div>
              <button onClick={() => { logout(); navigate('/'); }} style={{
                background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)',
                padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>Logout</button>
            </div>
          ) : (
            <NavBtn active={location.pathname === '/auth'} onClick={() => navigate('/auth', { state: { mode: 'login' } })}>Sign In</NavBtn>
          )}
        </div>
      </nav>
      )}

      {/* Guest banner */}
      {!isAuthenticated && location.pathname !== '/auth' && !isGuestLanding && (
        <div style={{
          background: 'var(--card)', borderBottom: '1px solid var(--border)',
          padding: '8px 20px', fontSize: 13, color: 'var(--teal)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>You're browsing as a guest. Run 1 free backtest and explain 1 strategy per day, or <button
            onClick={() => navigate('/auth')}
            style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 700, cursor: 'pointer', fontSize: 13, padding: 0 }}
          >sign up</button> to unlock everything.</span>
        </div>
      )}

      {showMobileNotice && !isGuestLanding && (
        <div style={{
          background: 'rgba(245, 158, 11, 0.16)',
          borderBottom: '1px solid rgba(245, 158, 11, 0.45)',
          color: 'var(--text)',
          padding: '9px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          fontSize: 12,
        }}>
          <span>
            Some features are not optimized for mobile yet. For the best experience, please use the desktop version.
          </span>
          <button
            onClick={dismissMobileNotice}
            style={{
              background: 'transparent',
              border: '1px solid rgba(245, 158, 11, 0.55)',
              color: 'var(--text)',
              borderRadius: 6,
              padding: '4px 8px',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Page content */}
      <div ref={mainScrollRef} style={{ flex: 1, overflow: 'auto', overflowX: 'hidden', background: 'var(--bg)', minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: isAppShellRoute ? 1 : '1 0 auto', minHeight: 0 }}>
        <Routes>
          <Route path="/" element={isAuthenticated ? <HomePage /> : <LandingPage />} />
          <Route path="/regime-tracker" element={isAuthenticated ? <RegimeTrackerPage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/stock/:ticker" element={isAuthenticated ? <StockPage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/strategies" element={<StrategyList onEdit={handleEdit} onBacktest={handleBacktest} onRequiresSignup={handleRequiresSignup} />} />
          <Route path="/builder" element={<StrategyBuilder strategy={editingStrategy} onSaved={handleSaved} onFeedback={handleFeedback} onRunBacktest={handleBacktest} onViewResults={handleResult} />} />
          <Route path="/backtest" element={
            <BacktestRunner
              onResult={handleResult}
              preselectedStrategyId={backtestStrategyId}
              onRequiresSignup={handleRequiresSignup}
            />
          } />
          <Route path="/results" element={
            backtestResult ? (
              <BacktestResults
                backtest={backtestResult}
                onImproved={(strategy) => { setEditingStrategy(strategy); navigate('/builder'); }}
                onRerunResult={(result) => { setBacktestResult(result); }}
                onRequiresSignup={handleRequiresSignup}
                onPaperTradeStarted={(session) => {
                  track(Events.PAPER_TRADING_STARTED, { session_id: session?.id ?? null });
                  navigate(`/paper-trading/${session.id}`);
                }}
              />
            ) : <Navigate to="/" replace />
          } />
          <Route path="/analyst" element={
            <AnalystPage
              onStrategyCreated={(s) => { setEditingStrategy(s); navigate('/builder'); }}
              onRequiresSignup={handleRequiresSignup}
              onFeedback={handleFeedback}
            />
          } />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/pricing/success" element={<PricingPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/api-access" element={<ApiPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/profile" element={isAuthenticated ? <ProfilePage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/billing" element={isAuthenticated ? <BillingPage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/notifications" element={isAuthenticated ? <NotificationsPage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/messages" element={isAuthenticated ? <MessagesPage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/messages/:id" element={isAuthenticated ? <MessageThreadPage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/watchlists" element={isAuthenticated ? <WatchlistsPage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/markets" element={isAuthenticated ? <MarketsPage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/backtests" element={isAuthenticated ? <AllBacktestsPage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/paper-trading" element={isAuthenticated ? <PaperTradingPage /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          <Route path="/paper-trading/:id" element={isAuthenticated ? <PaperTradingDashboard /> : <Navigate to="/auth" state={{ from: location.pathname }} replace />} />
          {/* Publicly viewable so shared/social links (e.g. auto-posted tweets) work
              for logged-out visitors. The API only returns owner-less backtests to
              guests, and BacktestResults gates all tools/actions behind auth. */}
          <Route path="/backtests/:id" element={
            <BacktestByIdPage
              onImproved={(strategy) => { setEditingStrategy(strategy); navigate('/builder'); }}
              onRerunResult={(result) => { setBacktestResult(result); navigate('/results'); }}
              onRequiresSignup={handleRequiresSignup}
            />
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
        {/* Hide the footer on full-height "app shell" routes (chat / canvas fill the
            viewport with absolute/100% layouts, so a footer overlaps their controls). */}
        {!isAppShellRoute && <Footer />}
      </div>

      {/* Signup prompt modal */}
      {signupPrompt && (
        <SignupPrompt
          message={signupPrompt}
          onSignup={() => { setSignupPrompt(null); navigate('/auth'); }}
          onClose={() => setSignupPrompt(null)}
        />
      )}

      {/* Feedback modal */}
      {feedbackModal && (
        <FeedbackModal
          initialSubject={feedbackModal.subject}
          initialMessage={feedbackModal.message}
          onClose={() => setFeedbackModal(null)}
        />
      )}
    </div>
  );
}

function NavBtn({ children, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="nav-btn"
      style={{
        background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
        color: 'var(--text)',
        border: 'none',
        padding: '6px 14px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function BacktestByIdPage({ onImproved, onRerunResult, onRequiresSignup }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [backtest, setBacktest] = useState(null);
  const [loadError, setLoadError] = useState(null);

  React.useEffect(() => {
    if (authLoading) return;   // wait until we actually know if they're logged in
    let cancelled = false;
    getBacktest(id)
      .then((res) => { if (!cancelled) setBacktest(res.data); })
      .catch(() => {
        if (cancelled) return;
        // Guests can only load owner-less (shared) backtests; an owned one
        // 401/403/404s. Rather than a dead-end, send them to log in and return
        // here afterwards (e.g. from a "your strategy traded" email deep link).
        if (!isAuthenticated) {
          navigate(`/auth?next=/backtests/${id}`, { replace: true });
        } else {
          setLoadError('Backtest not found or still running.');
        }
      });
    return () => { cancelled = true; };
  }, [id, isAuthenticated, authLoading, navigate]);

  if (loadError) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>{loadError}</div>;
  }
  if (!backtest) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>;
  }
  if (backtest.status === 'running' || backtest.status === 'pending') {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
        This backtest is still running. You'll be notified when it's done.
      </div>
    );
  }
  return (
    <BacktestResults
      backtest={backtest}
      onImproved={onImproved}
      onRerunResult={onRerunResult}
      onRequiresSignup={onRequiresSignup}
    />
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BackgroundRunsProvider>
          <AppInner />
        </BackgroundRunsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
